import {
  dataTransformationContent,
  agenticAiContent,
  mcpContent,
  insuranceAgentsContent,
  mdmContent,
} from './blogContent2025.js';
import {
  aiWorkflowVsAgentsContent,
  generativeAiContent,
  ragContent,
  missionCriticalRagContent,
  agenticLlmContent,
} from './blogContent2023_2024.js';
import { trustStackContent } from './blogContent2026.js';

/**
 * Consolidated blog post seed data for the Shared Oxygen Blog Management System.
 * Research-grounded executive content (2023–2026).
 */

export const seedBlogs = [
  {
    title: 'The AI Stack Is Becoming A Trust Stack',
    imageUrl: '/images/blog/ai-trust-stack.svg',
    ogImageUrl: '/images/blog/ai-trust-stack-linkedin.png',
    imageAlt: 'Authority at the boundary — suggestion ends where commitment begins',
    content: trustStackContent,
    excerpt:
      'When AI reaches for the keyboard, intelligence stops being the bottleneck. Trust does. Suggestion ends where commitment begins.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2026-08-22').toISOString(),
    status: 'published',
    slug: 'ai-stack-becoming-trust-stack',
    tags: [
      'ai',
      'trust',
      'agentic-ai',
      'governance',
      'enterprise-ai',
      'autonomy',
      'operating-discipline',
    ],
    featured: true,
    seoTitle: 'The AI Stack Is Becoming A Trust Stack | Shared Oxygen',
    seoDescription:
      'As models become interchangeable, advantage shifts to the trust stack: delegated identity, policy, durable state, observability, evaluation, and recovery.',
    focusKeywords:
      'AI trust stack, agentic AI governance, enterprise AI trust, AI authority boundaries, verified work per dollar, AI operating discipline',
  },
  {
    title: 'Data Made Work Measurable. AI Makes It Executable.',
    imageUrl: '/images/blog/ai-flight-deck-data-transformation.svg',
    content: dataTransformationContent,
    excerpt:
      'AI shortens the distance between seeing and doing. The question is whether your data and your institution can act on what the machine surfaces before the quarter closes.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2025-11-16').toISOString(),
    status: 'published',
    slug: 'data-transformation-catalyst-ai-breaks-pattern',
    tags: ['ai', 'workforce-transformation', 'data-analytics', 'future-of-work', 'automation', 'digital-transformation', 'business-strategy'],
    featured: true,
    seoTitle:
      'Data Made Work Measurable. AI Makes It Executable | Shared Oxygen',
    seoDescription:
      'AI closes the loop between measurement and action. Use signal integrity, judgment velocity, and cycle lift to put AI under executive command.',
    focusKeywords:
      'AI workforce transformation, data-driven transformation, future of work, AI automation, workforce analytics, AI adoption, digital transformation',
  },
  {
    title: 'Model Context Protocol (MCP): Context As Operating Control',
    imageUrl: '/images/blog/mcp-context-control-plane.svg',
    content: mcpContent,
    excerpt:
      'MCP is plumbing only until you can answer what context an agent had when it acted. Context architecture is operating control, not an SDK detail.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2025-04-23').toISOString(),
    status: 'published',
    slug: 'model-context-protocol-mcp',
    tags: ['ai', 'mcp', 'context', 'protocols', 'systems'],
    featured: true,
  },
  {
    title: 'AI Agents For Insurance: Service Under Control',
    imageUrl: '/images/blog/insurance-agents-governance.svg',
    content: insuranceAgentsContent,
    excerpt:
      'Agents can speed intake, renewal prep, and claims support — if licensed professionals still own advice, binding, and the record an examiner would ask for.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2025-04-21').toISOString(),
    status: 'published',
    slug: 'ai-agents-for-insurance',
    tags: [
      'insurance',
      'ai-agents',
      'customer-engagement',
      'digital-transformation',
    ],
  },
  {
    title:
      'Mission-Critical RAG: Add Agents Only With Control',
    imageUrl: '/images/blog/mission-critical-rag-agents.svg',
    content: missionCriticalRagContent,
    excerpt:
      'RAG plus agents turns wrong answers into wrong actions. Govern retrieval, tool permissions, and review as one system — or stay at read-only RAG.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2024-04-15').toISOString(),
    status: 'published',
    slug: 'mission-critical-priority-rag-autonomous-agents',
    tags: ['rag', 'ai', 'autonomous-agents', 'innovation'],
  },
  {
    title: 'Generative AI: From Novelty To Operating Discipline',
    imageUrl: '/images/blog/generative-ai-discipline.svg',
    content: generativeAiContent,
    excerpt:
      'Model access is widespread. Operating discipline — data boundaries, evaluation, ownership, cost — is what separates production use from pilot theater.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2024-03-15').toISOString(),
    status: 'published',
    slug: 'advancements-in-generative-ai',
    tags: [
      'generative-ai',
      'innovation',
      'technology',
      'multimodal',
      'creativity',
    ],
  },
  {
    title: 'Retrieval-Augmented Generation (RAG): Evidence Before Output',
    imageUrl: '/images/blog/rag-evidence-discipline.svg',
    content: ragContent,
    excerpt:
      'RAG grounds generation in retrieved sources — Lewis et al., NeurIPS 2020. Corpus quality and retrieval testing matter more than model brand.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2024-10-10').toISOString(),
    status: 'published',
    slug: 'understanding-rag',
    tags: ['rag', 'ai', 'retrieval', 'llm', 'nlp'],
    readingTime: 5,
  },
  {
    title: 'Agentic LLMs: Start With Authority Boundaries',
    imageUrl: '/images/blog/agentic-llm-authority.svg',
    content: agenticLlmContent,
    excerpt:
      'Agentic LLMs loop through reason, action, and observation — ReAct, Yao et al., 2022. Define authority and owners before you choose a platform.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2024-12-12').toISOString(),
    status: 'published',
    slug: 'agentic-llm-getting-started',
    tags: ['agentic-llm', 'ai', 'autonomous-agents', 'strategy'],
  },
  {
    title: 'AI Workflow Vs. AI Agents: What’s The Difference?',
    imageUrl: '/images/blog/ai-workflow-vs-agents.svg',
    content: aiWorkflowVsAgentsContent,
    excerpt:
      'Workflows follow a diagram. Agents choose tools inside bounds. The operating model should pick the mix — not the vendor slide.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2023-10-15').toISOString(),
    status: 'published',
    slug: 'ai-workflow-vs-agents',
    tags: ['ai-workflow', 'ai-agents', 'automation', 'strategy'],
  },
  {
    title: 'AI And Master Data Management: Govern The Reference Layer',
    imageUrl: '/images/blog/mdm-reference-layer.svg',
    content: mdmContent,
    excerpt:
      'Duplicate records and stale mappings were always costly. AI just makes the mistake reach the client before a human catches it.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2025-03-22').toISOString(),
    status: 'published',
    slug: 'ai-master-data-management',
    tags: ['mdm', 'ai', 'data-governance', 'enterprise'],
  },
  {
    title:
      'Agentic AI: Competitive Advantage Requires Command',
    imageUrl: '/images/blog/agentic-ai-command-map.svg',
    content: agenticAiContent,
    excerpt:
      'Copilots suggest; agents act. Competitive advantage sits in who you let touch production systems — and who answers when something breaks.',
    author: 'Shared Oxygen',
    publishedDate: new Date('2025-09-24').toISOString(),
    status: 'published',
    slug: 'agentic-ai-competitive-advantage',
    tags: [
      'agentic-ai',
      'autonomous-agents',
      'digital-transformation',
      'business-strategy',
      'roi',
      'enterprise-ai',
    ],
    featured: true,
    seoTitle:
      'Agentic AI: Competitive Advantage Requires Command | Shared Oxygen',
    seoDescription:
      'Agentic AI creates advantage only when authority, tools, evidence, and owners are explicit before production use.',
    focusKeywords:
      'agentic AI, autonomous agents, competitive advantage, enterprise AI, business transformation, AI ROI, productivity gains',
  },
];
